import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getVisiblePageNumbers } from 'lib/pagination-visible-pages';
import { PAGE_SIZE_OPTIONS, type PageSizeOption } from 'lib/page-size-options';

const btnBase =
  'inline-flex items-center justify-center min-w-[2.25rem] h-9 px-3 rounded-lg text-sm font-semibold border transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none';
const btnIdle =
  'border-line bg-card text-body hover:bg-subtle hover:text-heading hover:border-indigo-500/30';
const btnActive = 'border-indigo-600 bg-indigo-600 text-white shadow-sm';
const btnNav = 'gap-1 px-3.5 text-xs font-bold uppercase tracking-wide';

interface CursorPaginationProps {
  page: number;
  totalPages: number;
  total: number;
  itemCount: number;
  limit: number;
  loading?: boolean;
  hasMore?: boolean;
  onFirst: () => void;
  onPageChange: (page: number) => void;
  onLast: () => void;
  onLimitChange?: (limit: PageSizeOption) => void;
}

const CursorPagination: React.FC<CursorPaginationProps> = ({
  page,
  totalPages,
  total,
  itemCount,
  limit,
  loading = false,
  hasMore = false,
  onFirst,
  onPageChange,
  onLast,
  onLimitChange,
}) => {
  const effectiveTotal =
    total > 0 ? total : itemCount === 0 ? 0 : (page - 1) * limit + itemCount + (hasMore ? 1 : 0);
  const effectiveTotalPages =
    total > 0
      ? Math.max(1, totalPages)
      : Math.max(1, page + (hasMore ? 1 : 0), totalPages);
  const visiblePages = getVisiblePageNumbers(page, effectiveTotalPages);
  const rangeStart = effectiveTotal === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd =
    effectiveTotal === 0 ? 0 : Math.min((page - 1) * limit + itemCount, effectiveTotal);
  const lastDisabled =
    loading || page >= effectiveTotalPages || (total <= 0 && !hasMore && page >= effectiveTotalPages);

  if (effectiveTotal === 0 && itemCount === 0) {
    return (
      <div className="px-5 py-3.5 sm:px-6 border-t border-line bg-subtle/30">
        <p className="text-xs text-muted font-medium text-center sm:text-left">No records</p>
      </div>
    );
  }

  return (
    <div className="px-5 py-3.5 sm:px-6 border-t border-line bg-subtle/30 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <p className="text-xs text-muted font-medium text-center sm:text-left">
          Showing{' '}
          <span className="font-bold text-heading tabular-nums">
            {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}
          </span>{' '}
          of <span className="font-bold text-heading tabular-nums">{effectiveTotal.toLocaleString()}</span>
        </p>
        {onLimitChange && (
          <label className="inline-flex items-center gap-2 text-xs text-muted font-medium">
            <span>Per page</span>
            <select
              value={limit}
              disabled={loading}
              onChange={(e) => onLimitChange(Number(e.target.value) as PageSizeOption)}
              className="h-8 rounded-lg border border-line bg-card px-2 text-xs font-semibold text-heading focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <nav className="flex flex-wrap items-center justify-center gap-1" aria-label="Pagination">
        <button
          type="button"
          disabled={loading || page <= 1}
          onClick={onFirst}
          className={`${btnBase} ${btnNav} ${btnIdle}`}
        >
          First
        </button>

        <button
          type="button"
          disabled={loading || page <= 1}
          onClick={() => onPageChange(page - 1)}
          className={`${btnBase} ${btnIdle}`}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>

        {visiblePages.map((p) => (
          <button
            key={p}
            type="button"
            disabled={loading}
            onClick={() => onPageChange(p)}
            className={`${btnBase} ${p === page ? btnActive : btnIdle}`}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        ))}

        <button
          type="button"
          disabled={loading || page >= effectiveTotalPages}
          onClick={() => onPageChange(page + 1)}
          className={`${btnBase} ${btnIdle}`}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>

        <button
          type="button"
          disabled={lastDisabled}
          onClick={onLast}
          className={`${btnBase} ${btnNav} ${btnIdle}`}
        >
          Last
        </button>
      </nav>
    </div>
  );
};

export default CursorPagination;
